import { forwardRef, useEffect, useRef, useState } from 'react';
import { CAN_EDIT_ACCOUNT_TYPES } from '@linearb/linjs-common';
import { WidgetWrapper } from './CustomWidget.style';
import { Flex } from 'components/base/layout-components';
import Benchmark from './Benchmark/Benchmark';
import useCustomWidget from './useCustomWidget';
import BaseWidget from '../BaseWidget';
import Header from './Header/Header';
import withPermissions from 'connected-components/HOC/PermissionsHOC';
import ComingSoon from './EmptyStates/ComingSoon';
import useEditableWidget from '../useEditableWidget';
import { useDispatch } from 'react-redux';
import { selectTeamId } from 'state/actions';
import useGoalsCustomWidget from '../GoalsCustomWidget/useGoalsCustomWidget';
import usePMCustomWidget from '../PMCustomWidget/usePMCustomWidget';
import useWidgetMetricsData from '../useWidgetMetricsData';
import EditCalculationStrategy from '../useEditableWidget/EditCalculationStrategy/EditCalculationStrategy';
import { isMedianMetric } from '../../custom-report.util';
import NoDataInfoBox from './EmptyStates/NoDataInfoBox';
import useReportedCustomWidget from '../ReportedCustomWidget/useReportedCustomWidget';

const useWidgetByMetricType = {
  default: useCustomWidget,
  teamsGoals: useGoalsCustomWidget,
  customPmMetrics: usePMCustomWidget,
  reportedMetrics: useReportedCustomWidget
};

const CustomWidget = forwardRef(
  (
    {
      graph,
      filters,
      metrics,
      metricsTotal,
      loadingMetrics,
      iterations = [],
      isInspection = false,
      shouldHideTitle = false,
      shouldHideCalcStrategy = false,
      shouldHideBenchmark = false,
      isEditingEnabled = true,
      shouldHideClickableMessage,
      events = {}, // custom events: {onAreaSelect, onPointClick}
      ...additionalProps
    },
    ref
  ) => {
    const dispatch = useDispatch();
    const useWidget =
      useWidgetByMetricType[graph.metricType] ?? useWidgetByMetricType.default;

    const widgetMetrics = useWidgetMetricsData({
      graph,
      filters,
      metrics,
      metricsTotal,
      loadingMetrics,
      iterations
    });

    const {
      setColor,
      setType,
      setAgg,
      graphEditConfig,
      editConfig,
      isEditable,
      EditSection,
      editableWidget
    } = useEditableWidget({
      graph: widgetMetrics.graphWithAggregation,
      filters,
      ...additionalProps
    });

    const {
      headerProps,
      benchmarkProps = null,
      graphProps,
      EmptyState,
      setIsHover,
      missingGraphData,
      isHover,
      goToSettings,
      widgetExtraElements = null,
      noMatchResults = false,
      ...rest
    } = useWidget({
      graph: editableWidget,
      filters,
      editConfig,
      widgetMetrics,
      events,
      shouldHideClickableMessage,
      isInspection,
      ...additionalProps
    });
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(Infinity);
    const infoBoxTextEllipsisAfter = `${containerWidth / 14}rem`;
    const { dataCyPrefix = '' } = headerProps;
    useEffect(() => {
      // Set the CustomWidget width whenever window size changes
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
      const handleResize = () => {
        if (containerRef.current) {
          setContainerWidth(containerRef.current.offsetWidth);
        }
      };

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, [containerRef]);

    const isMedian = isMedianMetric(widgetMetrics.name);

    const editCalculationStratefyProps = {
      onAggChange: setAgg,
      widget: editableWidget,
      isMedian,
      isHover
    };

    const RightSection = [
      withPermissions({
        WrappedComponent: missingGraphData ? (
          <ComingSoon />
        ) : isEditingEnabled ? (
          <EditSection
            isHover={isHover}
            editConfig={graphEditConfig}
            onColorChange={setColor}
            onGraphTypeChange={setType}
            onAggChange={setAgg}
            goToSettings={goToSettings}
            selectTeamId={id => dispatch(selectTeamId(id))}
            globallySelectedTeamId={filters.firstGloballySelectedTeamId}
            widget={editableWidget}
            {...rest}
            {...editConfig}
            isMedian={isMedianMetric(widgetMetrics.name)}
          />
        ) : (
          <></>
        ),
        requiredPermissions: EmptyState ? [] : CAN_EDIT_ACCOUNT_TYPES
      }),
      !shouldHideBenchmark && benchmarkProps && (
        <Benchmark {...benchmarkProps} />
      )
    ].filter(Boolean);

    const GraphTitleSection = loadingMetrics ? null : (
      <>
        <Flex justify='space-between' items='flex-start' ref={containerRef}>
          <Header {...headerProps} shouldHideTitle={shouldHideTitle} />
          {!shouldHideCalcStrategy && (
            <EditCalculationStrategy {...editCalculationStratefyProps} />
          )}

          {isMedian ? RightSection.reverse() : RightSection}
        </Flex>
        <NoDataInfoBox
          showNoDataInfoBox={noMatchResults}
          infoBoxTextEllipsisAfter={infoBoxTextEllipsisAfter}
        />
      </>
    );

    const graphContent = EmptyState ?? (
      <>
        {GraphTitleSection}
        <BaseWidget
          key={additionalProps.editMode}
          graph={editableWidget}
          graphProps={graphProps}
        />
        {widgetExtraElements}
      </>
    );

    return (
      <WidgetWrapper
        className='graph-box-wrapper'
        isEditable={!loadingMetrics && isEditable && isEditingEnabled}
        direction='column'
        data-testid='graph-box-wrapper'
        data-cy={`${dataCyPrefix}-graph-box-wrapper`}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        ref={ref}
        {...additionalProps}
      >
        {graphContent}
      </WidgetWrapper>
    );
  }
);

export default CustomWidget;
